import { AxTable } from "@dynata/axiom3";
import { ReactNode } from "react";
import { EditableDescription } from "../ad-hoc-projects/editable-description.tsx";
import { useUpdateAudience } from "../../hooks/audiences";
import { Link } from "react-router-dom";

// https://ant.design/components/table#column
interface ITableColumn {
  title: string;
  dataIndex: string;
  key: string;
  className?: string;
  render?: (text: string, record: IAudiencesTableData) => ReactNode;
}

// Data structure for each row in the audiences table
export interface IAudiencesTableData {
  context_id: string;
  description: string;
  country: string;
  language: string;
}

// Props for the AudiencesTable component
export interface AudiencesTableProps {
  data: IAudiencesTableData[];
  loading: boolean;
  accountNumber: number;
  projectId: string;
}

// Define the columns for the audiences table
function getColumns(accountNumber: number, projectId: string): ITableColumn[] {
  return [
    {
      title: "ctx=",
      dataIndex: "context_id",
      key: "context_id",
      render: (contextId) => {
        const link = accountNumber
          ? `/accounts/${accountNumber}/dashboard?ctx=${contextId}`
          : `/dashboard?ctx=${contextId}`;

        return <Link to={link}>{contextId}</Link>;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description, record) => {
        const { mutateAsync: updateAudience } = useUpdateAudience(
          accountNumber,
          parseInt(projectId),
          record.context_id,
        );

        const submitUpdate = async (value: string) => {
          return await updateAudience({ description: value });
        };

        return (
          <EditableDescription
            initialValue={description}
            onSubmit={submitUpdate}
          >
            {description}
          </EditableDescription>
        );
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
    },
  ];
}

// AudiencesTable component
export function AudiencesTable({
  data,
  loading,
  accountNumber,
  projectId,
}: AudiencesTableProps) {
  const columns = getColumns(accountNumber, projectId);
  const tableProps = { dataSource: data, columns, pagination: false, loading };

  return <AxTable {...tableProps} />;
}
